import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiClient } from '../../api/client';
import { OrderTicketEditor, TicketRow } from './OrderTicketEditor';
import { ChapterDropdown } from './ChapterDropdown';

interface OrderTier {
  id: string;
  name: string;
  price: number;
}

interface OrderTicket {
  id: string;
  ticketNumber: number;
  tierName: string;
  priceAtPurchase: number;
  status: string;
  registrationId: string | null;
  reconciled: boolean;
}

interface OrderSummary {
  id: string;
  referenceId: string;
  purchaserName: string;
  purchaserEmail: string;
  purchaserPhone: string;
  ticketCount: number;
  seatedCount: number;
  status: 'Pending' | 'Confirmed' | 'Cancelled';
  creationType?: 'Manual' | 'Upload';
  chapterAffiliation?: string | null;
  amount?: string | null;
  isComplimentary?: boolean;
  complimentaryReason?: string | null;
  createdAt: string;
  tier: OrderTier;
  tickets?: OrderTicket[];
  createdByUser?: { id: string; fullName: string; email: string } | null;
  stats?: {
    totalTickets: number;
    provisioned: number;
    notProvisioned: number;
    seated: number;
    notSeated: number;
  };
}

type OrderSortKey = 'date' | 'ticketCount' | 'seatingStatus';

const SORT_OPTIONS: { key: OrderSortKey; label: string }[] = [
  { key: 'date', label: 'Date (newest)' },
  { key: 'ticketCount', label: 'Ticket count (desc)' },
  { key: 'seatingStatus', label: 'Seating status' },
];

function seatingStatusRank(order: OrderSummary): number {
  if (order.ticketCount === 0) return 2;
  if (order.seatedCount === 0) return 0; // none seated → first
  if (order.seatedCount < order.ticketCount) return 1; // partial
  return 2; // all seated
}

function getSeatingIndicator(order: OrderSummary): { color: string; title: string } {
  if (order.ticketCount === 0) return { color: '#9ca3af', title: 'No tickets' };
  if (order.seatedCount >= order.ticketCount) return { color: '#16a34a', title: 'All seated' };
  if (order.seatedCount > 0) return { color: '#eab308', title: `${order.seatedCount}/${order.ticketCount} seated` };
  return { color: '#dc2626', title: 'None seated' };
}

interface OrdersListProps {
  onOrderChanged?: () => void;
  refreshTrigger?: number;
}

const STATUS_COLORS: Record<string, React.CSSProperties> = {
  Pending: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  Confirmed: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  Cancelled: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1.5rem',
  },
  collapseHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
  },
  collapseArrow: {
    fontSize: '0.75rem',
    color: '#6b7280',
    transition: 'transform 0.2s',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: 0,
    marginTop: 0,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.9rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  tableContainer: {
    overflowX: 'auto' as const,
    marginTop: '0.75rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
  },
  th: {
    padding: '0.6rem 0.75rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151',
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
  },
  actionButton: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.4rem',
  },
  confirmButton: {
    backgroundColor: '#0f2d1f',
    color: '#fff',
  },
  cancelButton: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#6b7280',
    fontSize: '0.85rem',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '95%',
    maxWidth: '1050px',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
  },
  modalTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#0f2d1f',
    margin: 0,
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    lineHeight: 1,
  },
  modalOrderInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    fontSize: '0.83rem',
    color: '#374151',
  },
  modalInfoLabel: {
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.15rem',
  },
  ticketsLoading: {
    padding: '1rem',
    textAlign: 'center' as const,
    color: '#6b7280',
    fontSize: '0.8rem',
  },
  sourceBadge: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
  },
  sourceBadgeManual: {
    backgroundColor: '#f3f4f6',
    color: '#374151',
  },
  sourceBadgeUpload: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  editFormSection: {
    marginBottom: '1.25rem',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  editFormGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.75rem',
  },
  editFormGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  editFormLabel: {
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.15rem',
  },
  editFormInput: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
  },
  saveButton: {
    padding: '0.4rem 0.85rem',
    backgroundColor: '#0f2d1f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '0.75rem',
  },
  saveButtonDisabled: {
    padding: '0.4rem 0.85rem',
    backgroundColor: '#9ca3af',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'not-allowed',
    marginTop: '0.75rem',
  },
  saveSuccess: {
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '0.4rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    marginTop: '0.5rem',
    display: 'inline-block',
  },
  linkButton: {
    padding: '0.35rem 0.7rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: '#f0f9ff',
    color: '#1e40af',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  linkForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginTop: '0.5rem',
  },
  linkInput: {
    padding: '0.35rem 0.6rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.82rem',
    width: '160px',
  },
  linkSubmitButton: {
    padding: '0.35rem 0.7rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: '#0f2d1f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  linkCancelButton: {
    padding: '0.35rem 0.5rem',
    fontSize: '0.9rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    lineHeight: 1,
  },
  linkSuccess: {
    color: '#166534',
    fontSize: '0.8rem',
    marginTop: '0.35rem',
  },
  linkError: {
    color: '#991b1b',
    fontSize: '0.8rem',
    marginTop: '0.35rem',
  },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function OrdersList({ onOrderChanged, refreshTrigger }: OrdersListProps) {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listCollapsed, setListCollapsed] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<OrderSortKey>('date');
  const [compOnlyFilter, setCompOnlyFilter] = useState(false);

  // Order edit state (Task 7.2)
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editChapter, setEditChapter] = useState('');
  const [editChapterFreeText, setEditChapterFreeText] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editSuccess, setEditSuccess] = useState('');

  // Link orphan ticket state
  const [linkMode, setLinkMode] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkMessage, setLinkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const fetchOrders = useCallback(async () => {
    try {
      const result = await apiClient<OrderSummary[]>('/legacy-ball/orders');
      setOrders(result);
      setError('');
    } catch {
      setError('Could not load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, refreshTrigger]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedOrderId) {
        setSelectedOrderId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedOrderId]);

  const handleRowClick = async (order: OrderSummary) => {
    setSelectedOrderId(order.id);
    setTicketsError('');
    setEditSuccess('');

    // Reset link orphan ticket state
    setLinkMode(false);
    setLinkInput('');
    setLinkMessage(null);

    // Initialize edit fields from order data
    setEditName(order.purchaserName);
    setEditEmail(order.purchaserEmail);
    setEditPhone(order.purchaserPhone);

    // Resolve chapter affiliation into chapter/freeText
    const chapters = order.chapterAffiliation || '';
    // We'll treat it as a named chapter initially; the ChapterDropdown
    // will show it as-is if it matches a known chapter, or the user can switch to "Other"
    setEditChapter(chapters);
    setEditChapterFreeText('');

    // Fetch tickets if not already loaded
    if (!order.tickets) {
      setTicketsLoading(true);
      try {
        const detail = await apiClient<OrderSummary>(
          `/legacy-ball/orders/${order.id}`,
        );
        setOrders((prev) =>
          prev.map((o) =>
            o.id === order.id ? { ...o, tickets: detail.tickets } : o,
          ),
        );
      } catch {
        setTicketsError('Could not load tickets for this order.');
      } finally {
        setTicketsLoading(false);
      }
    }
  };

  const handleConfirm = async (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionLoading(orderId);
    try {
      await apiClient(`/legacy-ball/orders/${orderId}/confirm`, {
        method: 'POST',
      });
      await fetchOrders();
      onOrderChanged?.();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to confirm order.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionLoading(orderId);
    try {
      await apiClient(`/legacy-ball/orders/${orderId}/cancel`, {
        method: 'POST',
      });
      await fetchOrders();
      onOrderChanged?.();
    } catch {
      setError('Failed to cancel order.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTicketUpdated = async (orderId: string) => {
    // Re-fetch the order details to get updated ticket data
    try {
      const detail = await apiClient<OrderSummary>(
        `/legacy-ball/orders/${orderId}`,
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, tickets: detail.tickets } : o,
        ),
      );
      onOrderChanged?.();
    } catch {
      // Silently fail — the user can reopen modal to refresh
    }
  };

  const handleSaveOrderInfo = async () => {
    if (!selectedOrder) return;
    setEditSaving(true);
    setEditSuccess('');

    // Resolve chapter affiliation
    let chapterAffiliation: string | null | undefined;
    if (editChapter && editChapter !== 'Other') {
      chapterAffiliation = editChapter;
    } else if (editChapter === 'Other' && editChapterFreeText.trim()) {
      chapterAffiliation = editChapterFreeText.trim();
    } else if (!editChapter) {
      chapterAffiliation = null;
    }

    // Build payload with only dirty fields
    const body: Record<string, string | null> = {};
    if (editName !== selectedOrder.purchaserName) body.purchaserName = editName;
    if (editEmail !== selectedOrder.purchaserEmail) body.purchaserEmail = editEmail;
    if (editPhone !== selectedOrder.purchaserPhone) body.purchaserPhone = editPhone;

    const currentChapter = selectedOrder.chapterAffiliation || null;
    const resolvedChapter = chapterAffiliation !== undefined ? chapterAffiliation : null;
    if (resolvedChapter !== currentChapter) {
      body.chapterAffiliation = resolvedChapter;
    }

    if (Object.keys(body).length === 0) {
      setEditSaving(false);
      return;
    }

    try {
      await apiClient(`/legacy-ball/orders/${selectedOrder.id}`, {
        method: 'PATCH',
        body,
      });
      setEditSuccess('Order info saved successfully.');
      await fetchOrders();
      onOrderChanged?.();
    } catch {
      setEditSuccess('');
      setError('Failed to save order info.');
    } finally {
      setEditSaving(false);
    }
  };

  const selectedOrder = selectedOrderId
    ? orders.find((o) => o.id === selectedOrderId)
    : null;

  const sortedOrders = useMemo(() => {
    let filtered = [...orders];
    if (compOnlyFilter) {
      filtered = filtered.filter((o) => o.isComplimentary);
    }
    switch (sortKey) {
      case 'ticketCount':
        filtered.sort((a, b) => b.ticketCount - a.ticketCount);
        break;
      case 'seatingStatus':
        filtered.sort((a, b) => seatingStatusRank(a) - seatingStatusRank(b));
        break;
      case 'date':
      default:
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
    }
    return filtered;
  }, [orders, sortKey, compOnlyFilter]);

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkMessage(null);

    if (!linkInput.trim()) {
      setLinkMessage({ type: 'error', text: 'Please enter a ticket number.' });
      return;
    }

    if (!selectedOrder) return;

    setLinkLoading(true);
    try {
      await apiClient(`/order-data-management/orders/${selectedOrder.id}/link-ticket`, {
        method: 'POST',
        body: { ticketNumber: linkInput.trim() },
      });
      setLinkMessage({ type: 'success', text: 'Ticket linked successfully.' });
      setLinkInput('');
      linkInputRef.current?.focus();
      // Refresh tickets in modal
      await handleTicketUpdated(selectedOrder.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      const msg = apiErr?.message || 'An error occurred. Please try again.';
      setLinkMessage({ type: 'error', text: msg });
    } finally {
      setLinkLoading(false);
    }
  };

  // Determine if order edit fields are dirty
  const isOrderInfoDirty = (() => {
    if (!selectedOrder) return false;
    if (editName !== selectedOrder.purchaserName) return true;
    if (editEmail !== selectedOrder.purchaserEmail) return true;
    if (editPhone !== selectedOrder.purchaserPhone) return true;

    let resolvedChapter: string | null;
    if (editChapter && editChapter !== 'Other') {
      resolvedChapter = editChapter;
    } else if (editChapter === 'Other' && editChapterFreeText.trim()) {
      resolvedChapter = editChapterFreeText.trim();
    } else {
      resolvedChapter = null;
    }
    const currentChapter = selectedOrder.chapterAffiliation || null;
    if (resolvedChapter !== currentChapter) return true;

    return false;
  })();

  if (loading) {
    return <div style={styles.loading}>Loading orders...</div>;
  }

  return (
    <div style={styles.container}>
      <div
        style={styles.collapseHeader}
        onClick={() => setListCollapsed(!listCollapsed)}
        role="button"
        tabIndex={0}
        aria-expanded={!listCollapsed}
        aria-label={listCollapsed ? 'Expand orders list' : 'Collapse orders list'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setListCollapsed(!listCollapsed);
          }
        }}
      >
        <h3 style={styles.sectionTitle}>Orders ({orders.length})</h3>
        <span
          style={{
            ...styles.collapseArrow,
            transform: listCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </div>

      {!listCollapsed && (
        <>
          {error && <div style={{ ...styles.error, marginTop: '0.75rem' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem', marginBottom: '0.5rem', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compOnlyFilter}
                onChange={(e) => setCompOnlyFilter(e.target.checked)}
              />
              🎁 Complimentary only
            </label>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              Sort by:
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as OrderSortKey)}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                aria-label="Sort orders"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.th}>Reference ID</th>
                  <th style={styles.th}>Purchaser</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Tickets</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Seated</th>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Source</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedOrders.map((order) => {
                  const indicator = getSeatingIndicator(order);
                  return (
                  <tr
                    key={order.id}
                    style={styles.clickableRow}
                    onClick={() => handleRowClick(order)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                    }}
                  >
                    <td style={styles.td}>{order.referenceId}</td>
                    <td style={styles.td}>
                      {order.purchaserName}
                      {order.isComplimentary && (
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', backgroundColor: '#dbeafe', color: '#1e40af' }} title={order.complimentaryReason || 'Complimentary'}>🎁 COMP</span>
                      )}
                    </td>
                    <td style={styles.td}>{order.purchaserEmail}</td>
                    <td style={styles.td}>{order.ticketCount}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }} title={indicator.title}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: indicator.color,
                        }}
                        aria-label={indicator.title}
                      />
                      <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {order.seatedCount}/{order.ticketCount}
                      </span>
                    </td>
                    <td style={styles.td}>{order.tier.name}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...STATUS_COLORS[order.status],
                        }}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {order.creationType ? (
                        <span
                          style={{
                            ...styles.sourceBadge,
                            ...(order.creationType === 'Upload'
                              ? styles.sourceBadgeUpload
                              : styles.sourceBadgeManual),
                          }}
                        >
                          {order.creationType}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={styles.td}>{formatDate(order.createdAt)}</td>
                    <td style={styles.td}>
                      {order.status === 'Pending' && (
                        <>
                          <button
                            style={{
                              ...styles.actionButton,
                              ...styles.confirmButton,
                              ...(actionLoading === order.id
                                ? styles.disabledButton
                                : {}),
                            }}
                            onClick={(e) => handleConfirm(order.id, e)}
                            disabled={actionLoading === order.id}
                          >
                            Confirm
                          </button>
                          <button
                            style={{
                              ...styles.actionButton,
                              ...styles.cancelButton,
                              ...(actionLoading === order.id
                                ? styles.disabledButton
                                : {}),
                            }}
                            onClick={(e) => handleCancel(order.id, e)}
                            disabled={actionLoading === order.id}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {orders.length === 0 && (
              <div style={styles.emptyState}>
                No orders found. Create an order to get started.
              </div>
            )}
          </div>
        </>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div
          style={styles.modalOverlay}
          onClick={() => setSelectedOrderId(null)}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h4 style={styles.modalTitle}>
                Order {selectedOrder.referenceId}
              </h4>
              <button
                style={styles.modalCloseButton}
                onClick={() => setSelectedOrderId(null)}
                aria-label="Close order details"
              >
                ✕
              </button>
            </div>

            <div style={styles.editFormSection}>
              <div style={styles.editFormGrid}>
                <div style={styles.editFormGroup}>
                  <label style={styles.editFormLabel}>Purchaser Name</label>
                  <input
                    type="text"
                    style={styles.editFormInput}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    disabled={editSaving}
                  />
                </div>
                <div style={styles.editFormGroup}>
                  <label style={styles.editFormLabel}>Purchaser Email</label>
                  <input
                    type="email"
                    style={styles.editFormInput}
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    disabled={editSaving}
                  />
                </div>
                <div style={styles.editFormGroup}>
                  <label style={styles.editFormLabel}>Purchaser Phone</label>
                  <input
                    type="tel"
                    style={styles.editFormInput}
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    disabled={editSaving}
                  />
                </div>
                <div style={styles.editFormGroup}>
                  <label style={styles.editFormLabel}>Chapter Affiliation</label>
                  <ChapterDropdown
                    value={editChapter}
                    freeText={editChapterFreeText}
                    onChange={(chapter) => setEditChapter(chapter)}
                    onFreeTextChange={(text) => setEditChapterFreeText(text)}
                    disabled={editSaving}
                  />
                </div>
              </div>
              <div>
                <button
                  style={
                    !isOrderInfoDirty || editSaving
                      ? styles.saveButtonDisabled
                      : styles.saveButton
                  }
                  disabled={!isOrderInfoDirty || editSaving}
                  onClick={handleSaveOrderInfo}
                >
                  {editSaving ? 'Saving...' : 'Save Order Info'}
                </button>
                {editSuccess && (
                  <span style={styles.saveSuccess}>{editSuccess}</span>
                )}
              </div>
            </div>

            {/* Complimentary Toggle */}
            <div style={{ padding: '0.75rem 1rem', margin: '0.5rem 0', backgroundColor: selectedOrder.isComplimentary ? '#eff6ff' : '#f9fafb', border: `1px solid ${selectedOrder.isComplimentary ? '#93c5fd' : '#e5e7eb'}`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedOrder.isComplimentary || false}
                    onChange={async (e) => {
                      const newVal = e.target.checked;
                      try {
                        await apiClient(`/legacy-ball/orders/${selectedOrder.id}`, {
                          method: 'PATCH',
                          body: { isComplimentary: newVal, ...(!newVal ? { complimentaryReason: null } : {}) },
                        });
                        await fetchOrders();
                        onOrderChanged?.();
                      } catch {}
                    }}
                  />
                  🎁 Complimentary Order (excluded from revenue)
                </label>
                {selectedOrder.isComplimentary && selectedOrder.complimentaryReason && (
                  <div style={{ fontSize: '0.75rem', color: '#1e40af', marginTop: '0.2rem', marginLeft: '1.4rem' }}>
                    Reason: {selectedOrder.complimentaryReason}
                  </div>
                )}
              </div>
              {selectedOrder.isComplimentary && (
                <input
                  type="text"
                  placeholder="Reason (optional)"
                  defaultValue={selectedOrder.complimentaryReason || ''}
                  onBlur={async (e) => {
                    const reason = e.target.value.trim() || null;
                    if (reason !== (selectedOrder.complimentaryReason || null)) {
                      try {
                        await apiClient(`/legacy-ball/orders/${selectedOrder.id}`, {
                          method: 'PATCH',
                          body: { complimentaryReason: reason },
                        });
                        await fetchOrders();
                      } catch {}
                    }
                  }}
                  style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px', width: '200px' }}
                />
              )}
            </div>

            <div style={styles.modalOrderInfo}>
              <div>
                <div style={styles.modalInfoLabel}>Reference ID</div>
                <div>{selectedOrder.referenceId}</div>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Tier</div>
                <div>{selectedOrder.tier.name}</div>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Status</div>
                <span
                  style={{
                    ...styles.statusBadge,
                    ...STATUS_COLORS[selectedOrder.status],
                  }}
                >
                  {selectedOrder.status}
                </span>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Created</div>
                <div>{formatDate(selectedOrder.createdAt)}</div>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Amount</div>
                <div>{selectedOrder.amount ? `$${selectedOrder.amount}` : '—'}</div>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Tickets</div>
                <div>{selectedOrder.ticketCount}</div>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Source</div>
                <div>{selectedOrder.creationType || '—'}</div>
              </div>
              <div>
                <div style={styles.modalInfoLabel}>Created By</div>
                <div>{selectedOrder.createdByUser?.fullName || selectedOrder.createdByUser?.email || '—'}</div>
              </div>
            </div>

            {/* Provisioning & Seating Progress */}
            {selectedOrder.stats && (
              <div style={{ margin: '0.75rem 0', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                  <div style={{ flex: '1 1 auto', minWidth: '140px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Provisioning</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${selectedOrder.stats.totalTickets > 0 ? (selectedOrder.stats.provisioned / selectedOrder.stats.totalTickets) * 100 : 0}%`, backgroundColor: selectedOrder.stats.notProvisioned === 0 ? '#16a34a' : '#eab308', borderRadius: '4px', transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {selectedOrder.stats.provisioned}/{selectedOrder.stats.totalTickets}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>
                      {selectedOrder.stats.notProvisioned === 0 ? 'All guests provisioned' : `${selectedOrder.stats.notProvisioned} not provisioned`}
                    </div>
                  </div>
                  <div style={{ flex: '1 1 auto', minWidth: '140px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Seating</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${selectedOrder.stats.totalTickets > 0 ? (selectedOrder.stats.seated / selectedOrder.stats.totalTickets) * 100 : 0}%`, backgroundColor: selectedOrder.stats.notSeated === 0 ? '#16a34a' : '#3b82f6', borderRadius: '4px', transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {selectedOrder.stats.seated}/{selectedOrder.stats.totalTickets}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>
                      {selectedOrder.stats.notSeated === 0 ? 'All guests seated' : `${selectedOrder.stats.notSeated} not seated`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {ticketsLoading && (
              <div style={styles.ticketsLoading}>Loading tickets...</div>
            )}
            {ticketsError && (
              <div style={styles.error}>{ticketsError}</div>
            )}

            {/* Link Orphan Ticket section */}
            <div>
              {!linkMode ? (
                <button
                  style={styles.linkButton}
                  onClick={() => { setLinkMode(true); setLinkMessage(null); }}
                  aria-label="Link orphan ticket to this order"
                >
                  🔗 Link Orphan Ticket
                </button>
              ) : (
                <form onSubmit={handleLinkSubmit} style={styles.linkForm}>
                  <input
                    ref={linkInputRef}
                    type="text"
                    placeholder="e.g. 91 or TKT-091"
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    style={styles.linkInput}
                    disabled={linkLoading}
                    aria-label="Ticket number to link"
                    autoFocus
                  />
                  <button type="submit" disabled={linkLoading} style={styles.linkSubmitButton}>
                    {linkLoading ? '...' : 'Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLinkMode(false); setLinkMessage(null); }}
                    style={styles.linkCancelButton}
                  >
                    ✕
                  </button>
                </form>
              )}
              {linkMessage && (
                <div style={linkMessage.type === 'success' ? styles.linkSuccess : styles.linkError}>
                  {linkMessage.text}
                </div>
              )}
            </div>

            {selectedOrder.tickets && (
              <OrderTicketEditor
                orderId={selectedOrder.id}
                tickets={selectedOrder.tickets as TicketRow[]}
                onTicketUpdated={() => handleTicketUpdated(selectedOrder.id)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
